import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  Send, Plus, Trash2, Pencil, X, Save, Search, ChevronLeft, ChevronRight, GripVertical,
  RefreshCw, FileDown, Lock, Unlock,
} from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigation } from '../../context/NavigationContext';
import { useDatabase } from '../../context/DatabaseContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import { dynamicApi, dsPackageApi, transmittalApi } from '../../services/api';
import SearchSelect from '../../components/common/SearchSelect';
import { applySearch } from '../../utils/listSearch';
import { dayKey } from '../../utils/dateTime';
import StuiDateTime from '../../components/common/StuiDateTime';
import CollapsibleSectionHeader from './CollapsibleSectionHeader';
import exportTransmittalPdf from './transmittalPdfExporter';
import {
  buildTransmittalPdfData, buildRfiPdfData, buildLetterOfTransmittalPdfData,
} from './transmittalPdfData';
import TransmittalPdfDocument from './pdf/TransmittalPdfDocument';
import RfiPdfDocument, { RFI_IMPACT_OPTIONS } from './pdf/RfiPdfDocument';
import LetterOfTransmittalPdfDocument, { SENDING_TYPES } from './pdf/LetterOfTransmittalPdfDocument';
import StuiSelect from '../../components/common/StuiSelect';

/**
 * Transmittals — the Submittal, RFI and Material Transfer logs.
 *
 * One page serving all three kinds, chosen by the :type route segment. Each kind lists only
 * its own Transmittal_Log rows (Transmittal_Type), and its status picker offers only the
 * TransmittalStatus rows whose Applies_To matches — that discriminator is why statuses are a
 * table rather than a lookup category.
 *
 * Layout follows DsPackagePage: own module header with CRUD and a 6-dot, then the STUI
 * two-panel list view — searchable, collapsible list left, detail right in collapsible sections.
 *
 * Reads and writes go through the generic dynamic API: Transmittal_Log, Transmittal_Item and
 * Transmittal_Package are plain project tables registered in AppCfg_FieldMetadata. Only the
 * number and the publish stamp need the server (transmittalApi). Transmittal_Number is NOT NULL
 * and unique, so a new row goes in with a provisional placeholder and is numbered straight
 * after; the number endpoint treats anything not in PREFIX-NNNNN form as unnumbered.
 *
 * PUBLISHED is a populated Published stamp. A published transmittal is a record of what was
 * sent: the detail is read-only, the PDF drops its DRAFT watermark, and the only verbs are
 * Unpublish and Export. Delete is for drafts only.
 */

const TYPES = {
  submittal:           { key: 'Submittal',         label: 'Submittal Log',         noun: 'submittal' },
  rfi:                 { key: 'RFI',               label: 'RFI Log',               noun: 'RFI' },
  'material-transfer': { key: 'Material Transfer', label: 'Material Transfer Log', noun: 'material transfer' },
};

// A submittal transmittal has 11 fixed line-item rows on the printed form; more is a second
// transmittal, so the editor stops at 11 rather than growing a list the PDF cannot print.
const MAX_SUBMITTAL_ITEMS = 11;

const SUBMITTAL_CHECKS = [
  ['chk_Submitted_Review',   'Submitted for Review & Approval'],
  ['chk_Resubmitted_Review', 'ReSubmitted for Review & Approval'],
  ['chk_Substitution',       'Substitution Involved'],
  ['chk_Resubmitted_Record', 'ReSubmitted for Record / Closeout'],
  ['chk_Other_Left',         'Other (left)'],
  ['chk_LEED',               'LEED Submittal'],
  ['chk_Full_Submittal',     'Full Submittal'],
  ['chk_Partial_Submittal',  'Partial Submittal'],
  ['chk_Other_Right',        'Other (right)'],
];

// Field layouts per type. kind: text | textarea | date | fk | select | check | url.
// FK options come from the dynamic lookups endpoint; `options` on a select is the hardcoded list.
const COMMON_HEAD = [
  { key: 'title',             label: 'Title',            kind: 'text', col: 16 },
  { key: 'transmittalStatus', label: 'Status',           kind: 'status', col: 8 },
  { key: 'source',            label: 'Source (Project)', kind: 'fk', fk: 'Source', col: 12 },
];

const FIELDS = {
  Submittal: [
    ...COMMON_HEAD,
    { key: 'transfer_Party', label: 'Transfer Company', kind: 'fk', fk: 'Transfer_Party', col: 12 },
    { key: 'transfer_Date',  label: 'Date Submitted',   kind: 'date', col: 6 },
    { key: 'response_Date',  label: 'Response Date',    kind: 'date', col: 6 },
    { key: 'revision',       label: 'Revision',         kind: 'text', col: 4 },
    { key: 'qty_Required',   label: 'Qty Required',     kind: 'text', col: 4 },
    { key: 'qty_Actual',     label: 'Qty Actual',       kind: 'text', col: 4 },
    { key: 'resubmission',   label: 'Resubmission',     kind: 'text', col: 6 },
    ...SUBMITTAL_CHECKS.map(([key, label]) => ({ key, label, kind: 'check', col: 8 })),
    { key: 'comments',       label: 'Comments',         kind: 'textarea', col: 24, rows: 3 },
    { key: 'attachment_Url', label: 'Attachment',       kind: 'url', col: 24 },
  ],
  RFI: [
    ...COMMON_HEAD,
    { key: 'transfer_Party', label: 'Respondent',       kind: 'fk', fk: 'Transfer_Party', col: 12 },
    { key: 'transfer_Date',  label: 'Date',             kind: 'date', col: 6 },
    { key: 'response_Date',  label: 'Response Date',    kind: 'date', col: 6 },
    { key: 'owner',          label: 'Owner',            kind: 'text', col: 8 },
    { key: 'author',         label: 'Author',           kind: 'text', col: 8 },
    { key: 'discipline',     label: 'Discipline',       kind: 'text', col: 8 },
    { key: 'building_Area',  label: 'Building / Area',  kind: 'text', col: 8 },
    { key: 'reference',      label: 'Reference',        kind: 'text', col: 8 },
    { key: 'impact',         label: 'Impact',           kind: 'select', options: RFI_IMPACT_OPTIONS, col: 8 },
    { key: 'information_Requested', label: 'Information Requested', kind: 'textarea', col: 24, rows: 5 },
    { key: 'proposed_Solution',     label: 'Proposed Solution',     kind: 'textarea', col: 24, rows: 5 },
    { key: 'response',              label: 'Response',              kind: 'textarea', col: 24, rows: 5 },
    { key: 'attachment_Url', label: 'Attachment',       kind: 'url', col: 24 },
  ],
  'Material Transfer': [
    ...COMMON_HEAD,
    { key: 'transfer_Party', label: 'Transfer Company', kind: 'fk', fk: 'Transfer_Party', col: 12 },
    { key: 'transfer_Date',  label: 'Transfer Date',    kind: 'date', col: 6 },
    { key: 'sending_Type',   label: 'We Are Sending',   kind: 'select', options: SENDING_TYPES, col: 6 },
    { key: 'comments',       label: 'Comments',         kind: 'textarea', col: 24, rows: 3 },
  ],
};

const ITEM_COLS = [
  ['client_Spec',    'Client Spec Section'],
  ['spec_Paragraph', 'Spec Paragraph'],
  ['description',    'Title / Description'],
  ['manufacturer',   'Manufacturer'],
  ['submittal_Type', 'Submittal Type'],
  ['lead_Time',      'Lead Time'],
];

const FINAL_NUMBER = /^(SUB|RFI|TX)-\d{5,}$/;

// The dynamic API reads back camelCase (first letter lowered) and matches writes against the
// metadata field names, which all start upper-case. Raising the first letter is the inverse.
const toDbKey = k => k.charAt(0).toUpperCase() + k.slice(1);

// Unique, never mistaken for a real number, and inside Transmittal_Number's 20 characters.
const placeholderNumber = () =>
  `NEW-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`.toUpperCase();

// Transfer_Date / Response_Date are date columns: the form holds the literal 'YYYY-MM-DD'.
const dateOnly = v => dayKey(v, { valueZone: 'none' });

const formFromRow = (row, fields) => {
  const f = {};
  for (const fld of fields) {
    const v = row?.[fld.key];
    if (fld.kind === 'check') f[fld.key] = !!v;
    else if (fld.kind === 'date') f[fld.key] = dateOnly(v);
    else f[fld.key] = v ?? '';
  }
  return f;
};

const toDbValue = (fld, v) => {
  if (fld.kind === 'check') return !!v;
  if (v === '' || v === undefined) return null;
  if (fld.kind === 'fk' || fld.kind === 'status') return Number(v);
  return v;
};

const itemsFromRows = rows => (rows || [])
  .slice()
  .sort((a, b) => (a.item_Order ?? 0) - (b.item_Order ?? 0))
  .map(r => ({ id: r.id, ...Object.fromEntries(ITEM_COLS.map(([k]) => [k, r[k] ?? ''])) }));

const blankItem = () => ({ id: null, ...Object.fromEntries(ITEM_COLS.map(([k]) => [k, ''])) });

export default function TransmittalsPage() {
  const { type: typeSlug } = useParams();
  const { dbSlug, currentDb, dbDisplayName } = useDatabase();
  const { byDestination } = useNavigation();

  if (!TYPES[typeSlug]) {
    return <Navigate to={`/${dbSlug}/transmittals/submittal`} replace />;
  }
  // The log's navigation row names its writes (X-Nav-Key).
  const navKey = byDestination(`transmittals/${typeSlug}`)?.id;
  return <TransmittalsView key={typeSlug} type={TYPES[typeSlug]} navKey={navKey}
    client={currentDb?.organizationName || currentDb?.description || dbDisplayName || ''} />;
}

function TransmittalsView({ type, client, navKey }) {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const { trackTransfer } = useDataTransfer();
  const menuRef = useRef(null);
  const fields = FIELDS[type.key];
  const isSubmittal = type.key === 'Submittal';
  const isMT = type.key === 'Material Transfer';

  const auditCfg = useMemo(
    () => ({ headers: { 'X-Changed-By': currentUser?.email || '', 'X-Module-Name': type.label, ...(navKey ? { 'X-Nav-Key': navKey } : {}) } }),
    [currentUser, type.label, navKey]
  );

  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [fkOptions, setFkOptions] = useState({ Source: [], Transfer_Party: [] });
  const [packages, setPackages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionCollapsed, setSectionCollapsed] = useState({ info: false, items: false, packages: false });
  const toggleSection = key => setSectionCollapsed(s => ({ ...s, [key]: !s[key] }));
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Edit state. isNew = the + form, not yet written.
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Children of the selected transmittal.
  const [items, setItems] = useState([]);          // Submittal line items (Transmittal_Item)
  const [editItems, setEditItems] = useState([]);
  const [links, setLinks] = useState([]);          // Transmittal_Package rows
  const [editLinks, setEditLinks] = useState([]);  // DSPackage ids
  const [mtItems, setMtItems] = useState([]);      // live Tags + Panels on Transfer_No
  const [mtLoading, setMtLoading] = useState(false);

  // PDF data is built as soon as the detail loads, so the Export click goes straight to the
  // save dialog while it is still a user gesture — a read after the click loses the gesture.
  const [pdfData, setPdfData] = useState(null);
  const [version, setVersion] = useState(0);

  // ── Loads ─────────────────────────────────────────────────────────────────
  const loadRows = useCallback(async () => {
    try {
      const res = await dynamicApi.getFilteredEncoded('Transmittal_Log', 'Transmittal_Type', type.key);
      const data = (res.data || []).slice().sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      setRows(data);
      setVersion(v => v + 1);
    } catch (err) {
      notify.error(err?.response?.data?.error || `Failed to load the ${type.label}.`);
    }
  }, [type.key, type.label, notify]);

  const loadReference = useCallback(async () => {
    try {
      const [st, src, party] = await Promise.all([
        dynamicApi.getAll('TransmittalStatus'),
        dynamicApi.getLookups('Transmittal_Log', 'Source'),
        dynamicApi.getLookups('Transmittal_Log', 'Transfer_Party'),
      ]);
      setStatuses((st.data || [])
        .filter(s => s.applies_To === type.key && s.is_Active !== false)
        .sort((a, b) => (a.display_Order ?? 0) - (b.display_Order ?? 0)));
      setFkOptions({ Source: src.data || [], Transfer_Party: party.data || [] });
    } catch (err) {
      notify.error(`Failed to load transmittal pick lists: ${err.message}`);
    }
    if (isSubmittal) {
      try {
        const res = await dsPackageApi.list();
        setPackages(res.data || []);
      } catch { /* the package picker stays empty; the rest of the page still works */ }
    }
  }, [type.key, isSubmittal, notify]);

  useEffect(() => { loadRows(); loadReference(); }, [loadRows, loadReference]);

  // Transmittals belong to one project database; a stale list would show another project's.
  useEffect(() => {
    const onDbChange = () => {
      setSelectedId(null); setIsEditing(false); setIsNew(false);
      loadRows(); loadReference();
    };
    window.addEventListener('pmd-database-changed', onDbChange);
    return () => window.removeEventListener('pmd-database-changed', onDbChange);
  }, [loadRows, loadReference]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selected = rows.find(r => r.id === selectedId) || null;
  // A + form is never published, whatever row was selected before it opened.
  const isPublished = !isNew && !!selected?.published;

  // Children of the selected row.
  useEffect(() => {
    setItems([]); setLinks([]); setMtItems([]);
    if (!selected) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (isSubmittal) {
          const [it, ln] = await Promise.all([
            dynamicApi.getFiltered('Transmittal_Item', 'Transmittal_Log', selected.id),
            dynamicApi.getFiltered('Transmittal_Package', 'Transmittal_Log', selected.id),
          ]);
          if (cancelled) return;
          setItems(itemsFromRows(it.data));
          setLinks(ln.data || []);
        }
        if (isMT && selected.transmittal_Number) {
          setMtLoading(true);
          const [tags, panels] = await Promise.all([
            dynamicApi.getFilteredEncoded('Tags', 'Transfer_No', selected.transmittal_Number),
            dynamicApi.getFilteredEncoded('Panels', 'Transfer_No', selected.transmittal_Number),
          ]);
          if (cancelled) return;
          setMtItems([
            ...(tags.data || []).map(t => ({
              key: `t${t.id}`, source: 'Tag', tag: t.tag_ID || '', description: t.description || '', serial: t.serial_No || '',
            })),
            ...(panels.data || []).map(p => ({
              key: `p${p.id}`, source: 'Panel', tag: p.panel || '', description: p.description || '', serial: p.serial_No || '',
            })),
          ]);
        }
      } catch (err) {
        if (!cancelled) notify.error(`Failed to load transmittal details: ${err.message}`);
      } finally {
        if (!cancelled) setMtLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // `version` re-reads the children after a save; `selected` itself is a new object per load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, version, isSubmittal, isMT, notify]);

  // Prefetch the PDF data for the selected row.
  useEffect(() => {
    setPdfData(null);
    if (!selected || isEditing) return undefined;
    let cancelled = false;
    (async () => {
      try {
        let data;
        if (isSubmittal) data = await buildTransmittalPdfData(selected.id);
        else if (isMT) data = await buildLetterOfTransmittalPdfData(selected, mtItems);
        else data = await buildRfiPdfData(selected, { client });
        if (!cancelled) setPdfData(data);
      } catch (err) {
        if (!cancelled) notify.error(`Could not prepare the PDF: ${err.message}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, version, isEditing, mtItems, isSubmittal, isMT, client, notify]);

  // ── Search ────────────────────────────────────────────────────────────────
  const searchFields = useMemo(
    () => ['transmittal_Number', 'title', 'transfer_Party_Display', 'transmittalStatus_Display', 'source_Display'],
    []
  );
  const filtered = useMemo(
    () => applySearch(rows, searchTerm, searchFields),
    [rows, searchTerm, searchFields]
  );

  // ── Edit helpers ──────────────────────────────────────────────────────────
  const baseForm = useMemo(
    () => (isNew ? formFromRow({}, fields) : formFromRow(selected, fields)),
    [isNew, selected, fields]
  );
  const fieldDirty = key => isEditing && editForm[key] !== baseForm[key];
  const itemsDirty = isSubmittal && isEditing
    && JSON.stringify(editItems) !== JSON.stringify(isNew ? [] : items);
  const linkIds = useMemo(() => links.map(l => l.dSPackage ?? l.dsPackage).filter(Boolean), [links]);
  const linksDirty = isSubmittal && isEditing
    && JSON.stringify([...editLinks].sort()) !== JSON.stringify(isNew ? [] : [...linkIds].sort());
  const hasDirty = isEditing && (isNew || fields.some(f => fieldDirty(f.key)) || itemsDirty || linksDirty);

  const setField = (key, value) => setEditForm(f => ({ ...f, [key]: value }));

  const beginEdit = () => {
    if (!selected || isPublished) return;
    setEditForm(formFromRow(selected, fields));
    setEditItems(items.map(i => ({ ...i })));
    setEditLinks([...linkIds]);
    setIsNew(false);
    setIsEditing(true);
  };

  const beginNew = () => {
    const form = formFromRow({}, fields);
    // New transmittals start at the first status for the type (Development, by display order).
    if (statuses[0]) form.transmittalStatus = statuses[0].id;
    setEditForm(form);
    setEditItems([]);
    setEditLinks([]);
    setIsNew(true);
    setIsEditing(true);
  };

  const exitEdit = () => { setIsEditing(false); setIsNew(false); setEditForm({}); };

  const guardDiscard = (proceed) => {
    if (hasDirty) notify.confirm('Discard unsaved changes?', proceed);
    else proceed();
  };

  const selectRow = (id) => {
    if (id === selectedId && !isNew) return;
    if (isEditing) guardDiscard(() => { exitEdit(); setSelectedId(id); });
    else setSelectedId(id);
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveItems = async (transmittalId) => {
    const keep = editItems.filter(i => ITEM_COLS.some(([k]) => String(i[k] || '').trim() !== ''));
    const keepIds = new Set(keep.filter(i => i.id).map(i => i.id));
    const original = isNew ? [] : items;

    for (const gone of original.filter(i => !keepIds.has(i.id))) {
      await dynamicApi.delete('Transmittal_Item', gone.id, auditCfg);
    }
    for (let n = 0; n < keep.length; n++) {
      const it = keep[n];
      const body = { Transmittal_Log: transmittalId, Item_Order: n + 1 };
      for (const [k] of ITEM_COLS) body[toDbKey(k)] = String(it[k] || '').trim() || null;
      if (it.id) await dynamicApi.update('Transmittal_Item', it.id, body, auditCfg);
      else await dynamicApi.create('Transmittal_Item', body, auditCfg);
    }
  };

  const saveLinks = async (transmittalId) => {
    const want = new Set(editLinks);
    for (const l of (isNew ? [] : links)) {
      const pkgId = l.dSPackage ?? l.dsPackage;
      if (!want.has(pkgId)) await dynamicApi.delete('Transmittal_Package', l.id, auditCfg);
    }
    const have = new Set(isNew ? [] : linkIds);
    for (const pkgId of editLinks) {
      if (!have.has(pkgId)) {
        await dynamicApi.create('Transmittal_Package', { Transmittal_Log: transmittalId, DSPackage: pkgId }, auditCfg);
      }
    }
  };

  const handleSave = async () => {
    if (!hasDirty) return;
    setBusy(true);
    let id = selected?.id;
    try {
      const body = {};
      for (const fld of fields) {
        if (isNew || fieldDirty(fld.key)) body[toDbKey(fld.key)] = toDbValue(fld, editForm[fld.key]);
      }

      if (isNew) {
        body.Transmittal_Type = type.key;
        body.Transmittal_Number = placeholderNumber();
        const res = await dynamicApi.create('Transmittal_Log', body, auditCfg);
        id = res.data?.id;
        if (!id) throw new Error('The server did not return the new transmittal id.');
        try {
          const num = await transmittalApi.assignNumber(id, auditCfg);
          notify.success(`${num.data.transmittalNumber} created`);
        } catch (err) {
          // The row exists; numbering can be retried from the 6-dot rather than lost.
          notify.error(err?.response?.data?.error
            || 'Transmittal saved, but numbering failed. Use "Assign number" from the actions menu.');
        }
      } else if (Object.keys(body).length > 0) {
        await dynamicApi.update('Transmittal_Log', id, body, auditCfg);
      }

      if (isSubmittal) {
        if (isNew || itemsDirty) await saveItems(id);
        if (isNew || linksDirty) await saveLinks(id);
      }

      if (!isNew) notify.success(`${selected.transmittal_Number} saved`);
      exitEdit();
      setSelectedId(id);
      await loadRows();
    } catch (err) {
      const data = err?.response?.data;
      notify.error(data?.error || data?.message || err.message || 'Could not save the transmittal.');
      // A new row that was created before a child write failed is real now; show it.
      if (isNew && id) { exitEdit(); setSelectedId(id); await loadRows(); }
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = () => {
    if (!selected || isPublished) return;
    notify.confirm(
      `Delete ${selected.transmittal_Number}? Its line items and package links go with it.`,
      async () => {
        setBusy(true);
        try {
          await dynamicApi.delete('Transmittal_Log', selected.id, auditCfg);
          notify.success(`${selected.transmittal_Number} deleted`);
          exitEdit();
          setSelectedId(null);
          await loadRows();
        } catch (err) {
          notify.error(err?.response?.data?.error || 'Could not delete the transmittal.');
        } finally {
          setBusy(false);
        }
      }
    );
  };

  // ── Verbs ─────────────────────────────────────────────────────────────────
  const runVerb = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      notify.success(ok(res));
      await loadRows();
    } catch (err) {
      notify.error(err?.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const publish = () => runVerb(
    () => transmittalApi.publish(selected.id, auditCfg),
    () => `${selected.transmittal_Number} published`
  );

  const unpublish = () => notify.confirm(
    `Unpublish ${selected.transmittal_Number}? It becomes an editable draft again and its PDF prints DRAFT.`,
    () => runVerb(
      () => transmittalApi.unpublish(selected.id, auditCfg),
      () => `${selected.transmittal_Number} unpublished`
    )
  );

  const assignNumber = () => runVerb(
    () => transmittalApi.assignNumber(selected.id, auditCfg),
    res => `Numbered ${res.data.transmittalNumber}`
  );

  const exportPdf = () => {
    if (!pdfData) return;
    const cfg = isSubmittal
      ? { Document: TransmittalPdfDocument, filenamePrefix: 'Submittal-Transmittal' }
      : isMT
        ? { Document: LetterOfTransmittalPdfDocument, filenamePrefix: 'Letter-of-Transmittal' }
        : { Document: RfiPdfDocument, filenamePrefix: 'RFI' };
    const rows = isSubmittal ? items.length : isMT ? mtItems.length : undefined;
    trackTransfer(
      { name: selected?.transmittal_Number || cfg.filenamePrefix, format: 'pdf', rows },
      () => exportTransmittalPdf({ data: pdfData, watermark: isPublished ? null : 'DRAFT', ...cfg })
    ).catch(err => notify.error(`PDF export failed: ${err.message}`));
  };

  const menuItem = (key, icon, label, disabled, onClick, reason) => (
    <button
      key={key}
      className={`stui-action-menu-item ${disabled ? 'disabled' : ''}`}
      disabled={disabled}
      title={disabled ? (reason || '') : ''}
      onClick={() => { setMenuOpen(false); onClick(); }}
    >
      {icon}<span>{label}</span>
    </button>
  );

  const unnumbered = !isNew && !!selected && !FINAL_NUMBER.test(selected.transmittal_Number || '');

  // ── Field rendering ───────────────────────────────────────────────────────
  const renderField = (fld) => {
    const editing = isEditing;
    const dirty = fieldDirty(fld.key) ? ' dirty' : '';
    const value = editing ? editForm[fld.key] : baseForm[fld.key];

    let control;
    if (fld.kind === 'check') {
      control = (
        <label className="stui-checkbox">
          <input
            type="checkbox"
            className="stui-checkbox-input"
            checked={!!value}
            disabled={!editing}
            onChange={editing ? e => setField(fld.key, e.target.checked) : undefined}
          />
          {editing && fieldDirty(fld.key) && 'changed'}
        </label>
      );
    } else if (fld.kind === 'fk' || fld.kind === 'status') {
      const opts = fld.kind === 'status'
        ? statuses.map(s => ({ id: s.id, display: s.status_Name }))
        : (fkOptions[fld.fk] || []);
      const display = opts.find(o => String(o.id) === String(value))?.display
        || (!editing ? selected?.[`${fld.key}_Display`] : '') || '';
      control = editing ? (
        <SearchSelect
          value={value}
          displayValue={display}
          options={opts}
          onChange={v => setField(fld.key, v ?? '')}
          className={dirty.trim()}
        />
      ) : (
        <input className="stui-field-input" value={display} readOnly />
      );
    } else if (fld.kind === 'select') {
      control = editing ? (
        <StuiSelect className={`stui-field-input${dirty}`} value={value || ''}
          onChange={e => setField(fld.key, e.target.value)}>
          <option value="">-- Select --</option>
          {fld.options.map(o => <option key={o} value={o}>{o}</option>)}
        </StuiSelect>
      ) : (
        <input className="stui-field-input" value={value || ''} readOnly />
      );
    } else if (fld.kind === 'textarea') {
      control = (
        <textarea
          className={`stui-field-input resizable${dirty}`}
          rows={fld.rows || 3}
          value={value || ''}
          readOnly={!editing}
          onChange={editing ? e => setField(fld.key, e.target.value) : undefined}
        />
      );
    } else if (fld.kind === 'url' && !editing) {
      control = (
        <div className="stui-field-display">
          {value
            ? <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
            : <span className="stui-text-placeholder">No attachment</span>}
        </div>
      );
    } else if (fld.kind === 'date') {
      control = (
        <StuiDateTime
          mode="date"
          valueZone="none"
          value={value || ''}
          disabled={!editing}
          dirty={!!dirty}
          onChange={v => { if (editing) setField(fld.key, v); }}
        />
      );
    } else {
      control = (
        <input
          type="text"
          className={`stui-field-input${dirty}`}
          value={value || ''}
          readOnly={!editing}
          onChange={editing ? e => setField(fld.key, e.target.value) : undefined}
        />
      );
    }

    return (
      <div className={`stui-field-group stui-col-${fld.col}`} key={fld.key}>
        <label>{fld.label}</label>
        {control}
      </div>
    );
  };

  // ── Submittal line items ──────────────────────────────────────────────────
  const shownItems = isEditing ? editItems : items;
  const setItemCell = (idx, key, v) =>
    setEditItems(list => list.map((it, i) => (i === idx ? { ...it, [key]: v } : it)));

  const renderItems = () => (
    <div className="stui-col-24">
      <table className="stui-inner-table">
        <thead>
          <tr>
            <th className="stui-cell-center">#</th>
            {ITEM_COLS.map(([k, label]) => <th key={k}>{label}</th>)}
            {isEditing && <th className="stui-inner-table-col-actions" />}
          </tr>
        </thead>
        <tbody>
          {shownItems.length === 0 && (
            <tr className="stui-inner-table-empty">
              <td colSpan={ITEM_COLS.length + (isEditing ? 2 : 1)}>No line items.</td>
            </tr>
          )}
          {shownItems.map((it, idx) => (
            <tr key={it.id ?? `new-${idx}`} className={isEditing ? 'stui-inner-table-edit-row' : ''}>
              <td className="stui-cell-center">{idx + 1}</td>
              {ITEM_COLS.map(([k]) => (
                <td key={k}>
                  {isEditing ? (
                    <input
                      className="stui-field-input stui-inner-table-input-sm"
                      value={it[k] || ''}
                      onChange={e => setItemCell(idx, k, e.target.value)}
                    />
                  ) : (it[k] || '')}
                </td>
              ))}
              {isEditing && (
                <td className="stui-inner-table-cell-actions">
                  <button
                    className="stui-btn stui-btn-danger-subtle stui-btn-icon"
                    title="Remove line"
                    onClick={() => setEditItems(list => list.filter((_, i) => i !== idx))}
                  >
                    <X size={16} />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {isEditing && (
            <tr className="stui-inner-table-add-row">
              <td colSpan={ITEM_COLS.length + 2}>
                <button
                  className="stui-btn stui-btn-secondary stui-btn-sm"
                  disabled={editItems.length >= MAX_SUBMITTAL_ITEMS}
                  title={editItems.length >= MAX_SUBMITTAL_ITEMS
                    ? `The form holds ${MAX_SUBMITTAL_ITEMS} line items; raise a second transmittal for more.`
                    : ''}
                  onClick={() => setEditItems(list => [...list, blankItem()])}
                >
                  <Plus size={14} /> Add line ({editItems.length}/{MAX_SUBMITTAL_ITEMS})
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // ── Linked packages (Submittal) ───────────────────────────────────────────
  const pkgLabel = id => {
    const p = packages.find(x => x.id === id);
    return p ? `${p.dsPackage}${p.dsPackageDesc ? ` — ${p.dsPackageDesc}` : ''}` : `Package ${id}`;
  };
  const shownLinks = isEditing ? editLinks : linkIds;
  const pkgOptions = packages
    .filter(p => !editLinks.includes(p.id))
    .map(p => ({ id: p.id, display: pkgLabel(p.id) }));

  const renderPackages = () => (
    <div className="stui-col-24">
      <table className="stui-inner-table">
        <thead>
          <tr>
            <th>Submittal Package</th>
            <th>Rev</th>
            <th>Status</th>
            {isEditing && <th className="stui-inner-table-col-actions" />}
          </tr>
        </thead>
        <tbody>
          {shownLinks.length === 0 && (
            <tr className="stui-inner-table-empty">
              <td colSpan={isEditing ? 4 : 3}>No packages on this transmittal.</td>
            </tr>
          )}
          {shownLinks.map(id => {
            const p = packages.find(x => x.id === id);
            return (
              <tr key={id}>
                <td>{pkgLabel(id)}</td>
                <td>{p?.dsPackageRev ?? ''}</td>
                <td>{p?.dsStatus ?? ''}</td>
                {isEditing && (
                  <td className="stui-inner-table-cell-actions">
                    <button
                      className="stui-btn stui-btn-danger-subtle stui-btn-icon"
                      title="Remove package"
                      onClick={() => setEditLinks(list => list.filter(x => x !== id))}
                    >
                      <X size={16} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
          {isEditing && (
            <tr className="stui-inner-table-add-row">
              <td colSpan={4}>
                <SearchSelect
                  value={null}
                  displayValue=""
                  options={pkgOptions}
                  placeholder="-- Add a submittal package --"
                  onChange={v => { if (v) setEditLinks(list => [...list, Number(v)]); }}
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // ── Material Transfer live items ──────────────────────────────────────────
  const liveItems = isNew ? [] : mtItems;
  const renderMtItems = () => (
    <div className="stui-col-24">
      <table className="stui-inner-table">
        <thead>
          <tr>
            <th className="stui-cell-center">#</th>
            <th>Type</th>
            <th>Tag / Panel</th>
            <th>Description</th>
            <th>Serial Number</th>
          </tr>
        </thead>
        <tbody>
          {mtLoading && (
            <tr className="stui-inner-table-empty"><td colSpan={5}>Loading…</td></tr>
          )}
          {!mtLoading && liveItems.length === 0 && (
            <tr className="stui-inner-table-empty">
              <td colSpan={5}>
                {isNew
                  ? 'Save first. Items are the Tags and Panels whose Transfer No. equals this transmittal number.'
                  : `No Tags or Panels carry Transfer No. ${selected?.transmittal_Number || ''}. Enter it on the tag or panel to list it here.`}
              </td>
            </tr>
          )}
          {!mtLoading && liveItems.map((it, i) => (
            <tr key={it.key}>
              <td className="stui-cell-center">{i + 1}</td>
              <td>{it.source}</td>
              <td>{it.tag}</td>
              <td>{it.description}</td>
              <td>{it.serial}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  const showDetail = isNew || !!selected;
  const publishedCount = rows.filter(r => r.published).length;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Send size={20} />
          <h1>{type.label}</h1>
          <span className="stui-module-header-meta">
            {rows.length
              ? `${publishedCount} published / ${rows.length} transmittal${rows.length === 1 ? '' : 's'}`
              : ''}
          </span>
        </div>

        <div className="stui-module-header-actions">
          {/* + and Trash share the slot: Trash while editing an existing draft. */}
          {isEditing && !isNew ? (
            <button className="stui-crud-btn stui-delete-btn" onClick={handleDelete} disabled={busy}
              title={`Delete ${type.noun}`}>
              <Trash2 size={18} />
            </button>
          ) : (
            <button
              className={`stui-crud-btn ${isEditing ? 'disabled' : ''}`}
              onClick={() => !isEditing && beginNew()}
              disabled={isEditing || busy}
              title={`New ${type.noun}`}
            >
              <Plus size={18} />
            </button>
          )}

          <button
            className={`stui-crud-btn ${!isEditing && (!selected || isPublished) ? 'disabled' : ''}`}
            onClick={() => (isEditing ? guardDiscard(exitEdit) : beginEdit())}
            disabled={busy || (!isEditing && (!selected || isPublished))}
            title={isEditing ? 'Cancel' : isPublished ? 'Published — unpublish to edit' : 'Edit'}
          >
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>

          <button
            className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={!hasDirty || busy}
            title="Save"
          >
            <Save size={18} />
          </button>

          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                {menuItem('refresh', <RefreshCw size={16} />, 'Refresh', busy, () => { loadRows(); loadReference(); })}
                {menuItem('export', <FileDown size={16} />,
                  `Download PDF${selected && !isPublished ? ' (DRAFT)' : ''}`,
                  busy || isEditing || !selected || !pdfData, exportPdf,
                  !selected ? 'Select a transmittal.'
                    : isEditing ? 'Save or cancel your edits first.'
                    : 'Preparing the PDF…')}
                <div className="stui-action-menu-divider" />
                {unnumbered && !isPublished && menuItem('number', <RefreshCw size={16} />, 'Assign Number',
                  busy || isEditing, assignNumber)}
                {isPublished
                  ? menuItem('unpublish', <Unlock size={16} />, 'Unpublish',
                      busy || isEditing, unpublish, 'Finish editing first.')
                  : menuItem('publish', <Lock size={16} />, 'Publish',
                      busy || isEditing || !selected || unnumbered, publish,
                      !selected ? 'Select a transmittal.'
                        : isEditing ? 'Save or cancel your edits first.'
                        : 'Assign a number first.')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {/* LIST PANEL */}
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand list">
              <ChevronRight size={16} />
            </button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse list">
                <ChevronLeft size={16} />
              </button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input
                  className="stui-field-input"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search transmittals..."
                />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filtered.length === 0 ? (
                <div className="stui-list-empty">
                  {rows.length === 0 ? 'No transmittals yet' : 'No transmittals found'}
                </div>
              ) : filtered.map(r => (
                <button
                  key={r.id}
                  onClick={() => selectRow(r.id)}
                  className={`stui-list-item ${!isNew && selected?.id === r.id ? 'selected' : ''}`}
                >
                  <span className="stui-list-item-primary">{r.transmittal_Number}</span>
                  <span className="stui-list-item-secondary">
                    {[r.title, r.transmittalStatus_Display, r.published ? 'Published' : 'Draft']
                      .filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DETAIL PANEL */}
        <div className="stui-detail-panel">
          {showDetail ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <CollapsibleSectionHeader
                    label="Transmittal Information"
                    collapsed={sectionCollapsed.info}
                    onToggle={() => toggleSection('info')}
                  />
                </div>
                {!sectionCollapsed.info && <>
                  <div className="stui-field-group stui-col-8">
                    <label>Transmittal Number</label>
                    <input
                      className="stui-field-input stui-field-readonly"
                      value={isNew ? 'Assigned on save' : (selected?.transmittal_Number || '')}
                      readOnly
                    />
                  </div>
                  <div className="stui-field-group stui-col-4">
                    <label>Aging</label>
                    <input className="stui-field-input stui-field-readonly"
                      value={isNew ? '' : (selected?.aging ?? '')} readOnly />
                  </div>
                  <div className="stui-field-group stui-col-12">
                    <label>Published</label>
                    <div className="stui-field-display">
                      <span className={`stui-badge ${isPublished ? 'stui-badge-green' : 'stui-badge-amber'}`}>
                        {isPublished ? selected.published : 'Draft'}
                      </span>
                    </div>
                  </div>
                  {fields.map(renderField)}
                </>}

                {isSubmittal && <>
                  <div className="stui-col-24">
                    <CollapsibleSectionHeader
                      label={`Line Items (${shownItems.length}/${MAX_SUBMITTAL_ITEMS})`}
                      collapsed={sectionCollapsed.items}
                      onToggle={() => toggleSection('items')}
                    />
                  </div>
                  {!sectionCollapsed.items && renderItems()}

                  <div className="stui-col-24">
                    <CollapsibleSectionHeader
                      label="Submittal Packages"
                      collapsed={sectionCollapsed.packages}
                      onToggle={() => toggleSection('packages')}
                    />
                  </div>
                  {!sectionCollapsed.packages && renderPackages()}
                </>}

                {isMT && <>
                  <div className="stui-col-24">
                    <CollapsibleSectionHeader
                      label={`Items — Tags and Panels on this transfer (${liveItems.length})`}
                      collapsed={sectionCollapsed.items}
                      onToggle={() => toggleSection('items')}
                    />
                  </div>
                  {!sectionCollapsed.items && renderMtItems()}
                </>}
              </div>
            </div>
          ) : (
            <div className="stui-detail-empty">
              {rows.length === 0
                ? `No ${type.noun} transmittals in this project database yet. Use + to create one.`
                : 'Select a transmittal to view it.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
